/** @jsxImportSource @emotion/react */
"use client";

import { useTranslations } from "@i18n/react";
import { usePlayer } from "@packages/media";
import type { MouseEventHandler } from "react";
import { forwardRef, useCallback } from "react";

import type { ButtonProps } from "../../../../../components/_base/_core/Button/Button.emotion";
import {
  PauseIcon,
  PlayIcon,
} from "../../../../../components/_base/icons/Icon/MediaIcons";
import {
  coreMediaStyles,
  useMediaClass,
} from "../../../../utils/media.emotion";
import type { MediaButtonProps } from "../MediaButton/MediaButton.emotion";
import { MediaButton } from "../MediaButton/MediaButton.emotion";

export type PlayPauseProps = Omit<ButtonProps, "size" | "color"> & {
  size?: MediaButtonProps["size"];
};

export const PlayPause = forwardRef<HTMLButtonElement, PlayPauseProps>(
  ({ size: buttonSize = null, ...props }, ref) => {
    const player = usePlayer();
    const t = useTranslations();
    const mediaClass = useMediaClass();

    const handlePlayPause = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (e) => {
        e.stopPropagation();
        if (player?.paused) {
          player.play();
        } else {
          player?.pause();
        }
      },
      [player],
    );

    return (
      <MediaButton
        largeIcon={
          !player || player.paused ? <PlayIcon.XL /> : <PauseIcon.XL />
        }
        smallIcon={
          !player || player.paused ? <PlayIcon.Medium /> : <PauseIcon.Medium />
        }
        size={buttonSize}
        onClickCapture={handlePlayPause}
        css={[
          coreMediaStyles.playbackButton,
          buttonSize === "l" ||
          mediaClass.includes("expanded") ||
          mediaClass.includes("fullscreen")
            ? coreMediaStyles.playbackButtonL
            : null,
        ]}
        ref={ref}
        {...props}
        title={t(
          !player || player.paused
            ? "button_description_play"
            : "button_description_pause",
        )}
      />
    );
  },
);

PlayPause.displayName = "PlayPause";
